import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  basisPointsToPercent,
  percentToBasisPoints,
} from 'features/deals/DealStages/EventActionCards/utils';
import { ChartSkeletonLoader } from 'features/pages/market-data/components/ChartSkeleton';
import { URLMapDeprecated } from 'features/pages/market-data/shared';
import BidSummary from 'features/pages/marketplace/BiddingAndPricing/BidSummary/BidSummary';
import DownloadPriceYieldCurveButton from 'features/pages/marketplace/BiddingAndPricing/YieldMatrix/DownloadPriceYieldCurveButton';
import MaturityData from 'features/pages/marketplace/BiddingAndPricing/YieldMatrix/MaturityData';
import { YieldCurveData } from 'features/pages/marketplace/BiddingAndPricing/YieldMatrix/types';
import {
  SOFR_YIELD_CURVE_FILE,
  TREASURY_YIELD_CURVE_FILE,
  lookupAtPricePercent,
  normalizePriceYieldMatrix,
  normalizeYieldCurveData,
  rateAsBps,
  rateAsPercent,
} from 'features/pages/marketplace/BiddingAndPricing/YieldMatrix/utils';
import YieldToPriceChart from 'features/pages/marketplace/BiddingAndPricing/YieldToPriceChart';

import { RateType } from '__generated__/globalTypes';
import { GetMarketData } from 'query/__generated__/getMarketData';
import { CashFlowsValidValues } from './configurations/cashflow-inputs';

import { GetCashflowByPool } from 'features/pages/portfolio/CashFlowModeling/__generated__/getSelectedPoolCashflow_gql';
import { GetDealCashFlows } from './gql/__generated__/useDealCashflows_gql';
import { GetPortfolioCashFlows } from './gql/__generated__/usePortfolioCashflows_gql';

type GetCashflowByPool_user_company_pool_performance_summary_cashFlows = NonNullable<
  NonNullable<
    NonNullable<NonNullable<NonNullable<GetCashflowByPool>['user']>['company']>['pool']
  >['performance_summary']
>['cashFlows'];
type GetDealCashFlows_deal_performanceSummary_cashFlows = NonNullable<
  NonNullable<NonNullable<GetDealCashFlows>['deal']>['performanceSummary']
>['cashFlows'];
type GetPortfolioCashFlows_user_company_performanceSummary_cashFlows = NonNullable<
  NonNullable<
    NonNullable<NonNullable<GetPortfolioCashFlows>['user']>['company']
  >['performanceSummary']
>['cashFlows'];
interface YieldMatrixModelingProps {
  formValues: CashFlowsValidValues;
  onFormChange: (validatedFormValues: CashFlowsValidValues) => void;
  marketData?: GetMarketData;
  unpaidBalance: number;
  cashFlows?:
    | GetCashflowByPool_user_company_pool_performance_summary_cashFlows
    | GetDealCashFlows_deal_performanceSummary_cashFlows
    | GetPortfolioCashFlows_user_company_performanceSummary_cashFlows
    | undefined;
  netCoupon?: number;
}

const YieldMatrixModeling: React.FC<YieldMatrixModelingProps> = ({
  formValues,
  onFormChange,
  marketData,
  unpaidBalance = 0,
  cashFlows,
  netCoupon = 0,
}) => {
  const [chartWidth, setChartWidth] = useState(800);
  const chartParentRef = useRef<HTMLDivElement>(null);
  const [bidBasisPoints, setBidBasisPoints] = useState(-5000);
  const [ycChartData, setYcChartData] = useState<YieldCurveData | null>(null);
  const [ycChartDataMap, setYcChartDataMap] = useState<URLMapDeprecated>({});
  const ycChartDataUrl =
    formValues.rateType === RateType.RiskFree ? TREASURY_YIELD_CURVE_FILE : SOFR_YIELD_CURVE_FILE;
  const [spreadDuration, setSpreadDuration] = useState<number | undefined>(undefined);
  const [spread, setSpread] = useState<number | undefined>(undefined);
  const [yieldDuration, setYieldDuration] = useState<number | undefined>(undefined);
  const [netYield, setNetYield] = useState<number | undefined>(undefined);

  // Fetch market data URLs
  useEffect(() => {
    if (marketData && marketData.marketData) {
      const urlMap: URLMapDeprecated = {};
      Object.entries(marketData.marketData.urlMap).forEach(([key, value]) => {
        urlMap[key] = String(value);
      });
      setYcChartDataMap(urlMap);
    }
  }, [marketData]);

  // Fetch YC chart data
  useEffect(() => {
    if (ycChartDataUrl && ycChartDataMap[ycChartDataUrl]) {
      fetch(ycChartDataMap[ycChartDataUrl])
        .then((response) => response.json())
        .then((data) => {
          setYcChartData(normalizeYieldCurveData(data, ycChartDataUrl));
        });
    }
  }, [ycChartDataUrl, ycChartDataMap]);

  useEffect(() => {
    const updateChartWidth = () => {
      if (chartParentRef.current) {
        const parentWidth = chartParentRef.current.offsetWidth;
        setChartWidth(parentWidth - 310);
      }
    };

    updateChartWidth();
    window.addEventListener('resize', updateChartWidth);
    return () => window.removeEventListener('resize', updateChartWidth);
  }, []);

  // Normalize the raw matrix (dollar prices) onto price-percent space using
  // the pool balance (cents) once, at this seam. Everything downstream — the
  // chart and the bid lookup below — works in price-percent, so no balance
  // arithmetic happens at lookup time (the ENG-2915 trap).
  const yieldPoints = useMemo(
    () => normalizePriceYieldMatrix(cashFlows?.price_yield_matrix?.yield_matrix, unpaidBalance),
    [cashFlows, unpaidBalance],
  );
  const spreadPoints = useMemo(
    () => normalizePriceYieldMatrix(cashFlows?.price_yield_matrix?.spread_matrix, unpaidBalance),
    [cashFlows, unpaidBalance],
  );

  // Calculate yield from matrix when bidBasisPoints changes
  useEffect(() => {
    // The bid is already a price-percent — the same space the matrix is now in.
    const bidPercent = parseFloat(basisPointsToPercent(bidBasisPoints));

    // lookupAtPricePercent returns null off the curve / on an empty series, so
    // a refetch that shrinks the matrix to [] (ENG-2862) or moves its bounds
    // off the current bid clears the value instead of leaving a stale one.
    const yieldResult = lookupAtPricePercent(yieldPoints, bidPercent);
    setNetYield(yieldResult ? rateAsPercent(yieldResult.rate) : undefined);
    setYieldDuration(yieldResult?.durationYears);

    const spreadResult = lookupAtPricePercent(spreadPoints, bidPercent);
    setSpread(spreadResult ? rateAsBps(spreadResult.rate) : undefined);
    setSpreadDuration(spreadResult?.durationYears);
  }, [bidBasisPoints, yieldPoints, spreadPoints]);

  const bidPx = basisPointsToPercent(bidBasisPoints);

  // Loaded-but-empty (no loans match the selected asset class) is distinct
  // from still-loading: the chart can't plot an empty matrix — ENG-2862.
  const hasMatrixData = yieldPoints.length > 0;

  const handlePointClick = (pricePercent: number) => {
    const basisPoints = percentToBasisPoints(pricePercent.toFixed(3));
    setBidBasisPoints(basisPoints);
  };

  return (
    <div>
      <MaturityData
        ycChartData={ycChartData}
        formValues={formValues}
        onCurveSelect={onFormChange}
        headerAction={
          <DownloadPriceYieldCurveButton
            yieldPoints={yieldPoints}
            spreadPoints={spreadPoints}
            formValues={formValues}
          />
        }
      />

      <div className="flex flex-col gap-8 md:flex-row" ref={chartParentRef}>
        <div className="w-full">
          {cashFlows?.price_yield_matrix ? (
            hasMatrixData ? (
              <YieldToPriceChart
                yieldPoints={yieldPoints}
                spreadPoints={spreadPoints}
                bidPx={bidPx}
                width={chartWidth - 25}
                onPointClick={handlePointClick}
              />
            ) : (
              <div
                data-testid="yield-matrix-empty-state"
                className="flex h-64 items-center justify-center text-gray-500"
              >
                No data available
              </div>
            )
          ) : (
            <ChartSkeletonLoader height={500} width={chartWidth - 25} />
          )}
        </div>

        <div className="min-w-[300px] pr-4">
          <BidSummary
            netCoupon={netCoupon}
            unpaidBalance={unpaidBalance}
            netYield={netYield}
            spread={spread}
            yieldDuration={yieldDuration}
            spreadDuration={spreadDuration}
            participationPercent={100}
            bidBasisPoints={bidBasisPoints}
            servicingRate={Number(formValues.servicingRate)}
            defaultAssumptionType={formValues.defaultAssumptionType}
            cdr={formValues.cdr}
            prepaymentAssumptionType={formValues.prepaymentAssumptionType}
            cpr={formValues.cpr}
          />
        </div>
      </div>
    </div>
  );
};

export default YieldMatrixModeling;
